import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, message, Space, Upload, Tag, Input, Divider } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { projectApi, api, orderApi } from '../services/api';

const { TextArea } = Input;

const FinalReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [finalVideos, setFinalVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [episodesCount, setEpisodesCount] = useState(5);
  const [orderStatus, setOrderStatus] = useState('');
  const [order, setOrder] = useState(null);
  const [comments, setComments] = useState([]);
  const [episodeComments, setEpisodeComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const isOrderCompleted = orderStatus === 'completed';

  const parseReviewComment = (comment) => {
    if (!comment) return { episodes: {}, overall: '' };
    
    const result = { episodes: {}, overall: '' };
    const lines = comment.split(/\r?\n/).filter(line => line.trim());
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      const episodeMatch = trimmedLine.match(/^第(\d+)集[：:]\s*(.+)$/);
      if (episodeMatch) {
        result.episodes[episodeMatch[1]] = episodeMatch[2].trim();
      } else if (trimmedLine.includes('整体意见')) {
        result.overall = trimmedLine.replace(/^整体意见[：:]\s*/, '').trim();
      }
    });
    
    return result;
  };

  const loadProjectData = useCallback(async () => {
    try {
      setLoading(true);

      const projectResponse = await projectApi.getById(id);
      setProject(projectResponse.data);
      setFinalVideos(projectResponse.data.final_videos || []);
      
      if (projectResponse.data.order) {
        setOrderStatus(projectResponse.data.order.status);
        setOrder(projectResponse.data.order);
        
        // 加载评论
        if (projectResponse.data.order.id) {
          loadComments(projectResponse.data.order.id);
        }
      }

      let count = 5;
      const scriptContent = projectResponse.data.script_content;
      if (scriptContent) {
        const episodeMatches = scriptContent.match(/第\s*(\d+)\s*集/g);
        if (episodeMatches) {
          const episodeNumbers = episodeMatches.map(match => {
            const num = match.match(/(\d+)/);
            return num ? parseInt(num[1]) : 0;
          }).filter(num => num > 0);
          if (episodeNumbers.length > 0) {
            count = Math.max(...episodeNumbers);
          }
        }
      }
      setEpisodesCount(count);

    } catch (error) {
      message.error('加载项目数据失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadComments = async (orderId) => {
    try {
      const response = await orderApi.getComments(orderId);
      const allComments = response.data.comments || [];
      setComments(allComments);
      
      const grouped = {};
      allComments.forEach(comment => {
        if (comment.episode !== null && comment.episode !== undefined) {
          const key = String(comment.episode);
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(comment);
        }
      });
      setEpisodeComments(grouped);
    } catch (error) {
      console.error('加载评论失败:', error);
    }
  };

  const handleAddComment = async (episode) => {
    const inputId = `comment_input_${episode}`;
    const inputElement = document.getElementById(inputId);
    if (!inputElement || !inputElement.value.trim()) {
      message.warning('请输入回复内容');
      return;
    }
    
    if (!order?.id) return;
    
    try {
      setCommentLoading(prev => ({ ...prev, [episode]: true }));
      const response = await orderApi.addComment(order.id, { 
        content: inputElement.value.trim(),
        episode: episode
      });
      message.success('回复成功');
      inputElement.value = '';
      
      setEpisodeComments(prev => {
        const key = String(episode);
        const current = prev[key] || [];
        return { ...prev, [key]: [response.data.comment, ...current] };
      });
    } catch (error) {
      message.error(error.response?.data?.error || '回复失败');
    } finally {
      setCommentLoading(prev => ({ ...prev, [episode]: false }));
    }
  };

  useEffect(() => {
    if (id) {
      loadProjectData();
    }
  }, [id, loadProjectData]);

  const handleVideoUpload = async (file, episode) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('video', file);
      formData.append('project_id', id);
      formData.append('is_final', true);
      formData.append('episode', episode);

      const response = await api.post('/upload/video', formData, {
        headers: {
          'Content-Type': undefined,
        },
      });

      if (response.data.success) {
        message.success('成片上传成功');
        loadProjectData();
      } else {
        message.error('成片上传失败: ' + (response.data.message || response.data.error || '未知错误'));
      }
    } catch (error) {
      message.error('成片上传失败: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleVideoDelete = async (videoId) => {
    try {
      const response = await api.delete('/upload/video', {
        data: {
          project_id: parseInt(id),
          video_id: videoId
        }
      });
      
      if (response.data.success) {
        message.success('成片删除成功');
        loadProjectData();
      } else {
        message.error('成片删除失败: ' + (response.data.message || response.data.error || '未知错误'));
      }
    } catch (error) {
      console.error('删除错误:', error);
      message.error('成片删除失败: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  };

  const handleVideoDownload = (videoPath) => {
    if (!videoPath) return;
    const fullUrl = buildVideoUrl(videoPath);
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = `final_video_${id}.mp4`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildVideoUrl = (videoPath) => {
    if (!videoPath) return '';
    // 如果是完整的URL，直接返回
    if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
      return videoPath;
    }
    // 如果是 data/ 开头的路径，需要添加 /shortdrama 前缀
    if (videoPath.startsWith('data/')) {
      try {
        return '/shortdrama/' + videoPath.split('/').map(encodeURIComponent).join('/');
      } catch (e) {
        return '/shortdrama/' + videoPath;
      }
    }
    // 如果已经有 /shortdrama 前缀
    if (videoPath.startsWith('/shortdrama/')) {
      try {
        const parts = videoPath.replace('/shortdrama/', '').split('/');
        return '/shortdrama/' + parts.map(encodeURIComponent).join('/');
      } catch (e) {
        return videoPath;
      }
    }
    // 去掉开头的 / 避免重复
    let path = videoPath;
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    return '/' + path;
  };

  const getVideoByEpisode = (episode) => {
    return finalVideos.find(video => {
      const match = video.id.match(/ep(\d+)|第(\d+)集|(\d+)/i);
      if (match) {
        const videoEpisode = parseInt(match[1] || match[2] || match[3]);
        return videoEpisode === episode;
      }
      return false;
    });
  };

  return (
    <div style={{ padding: 24, background: '#0d0d12', minHeight: '100%' }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(`/project/${id}`)}
        style={{ marginBottom: 16, background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
      >
        返回项目管理
      </Button>

      <Card
        title={<span style={{ color: '#e8e8ec' }}>成片审核</span>}
        extra={
          isOrderCompleted ? (
            <Tag color="#ffd700" style={{ color: '#0d0d12' }}>审核已通过，不可修改</Tag>
          ) : (
            <Tag color="#00d4aa" style={{ color: '#0d0d12' }}>短剧成片列表</Tag>
          )
        }
        style={{ 
          background: '#1a1a24', 
          border: '1px solid #2a2a3a',
          borderRadius: 12
        }}
        headStyle={{ borderBottom: '1px solid #2a2a3a' }}
      >
        <div style={{ marginBottom: 16 }}>
          <span style={{ color: '#a0a0b0' }}>根据剧本信息，共 {episodesCount} 集</span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 24,
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 300px)'
        }}>
          {Array.from({ length: episodesCount }, (_, index) => {
            const episode = index + 1;
            const video = getVideoByEpisode(episode);

            return (
              <Card
                key={episode}
                title={<span style={{ color: '#e8e8ec', fontSize: 14 }}>第 {episode} 集</span>}
                style={{ 
                  borderRadius: 8, 
                  overflow: 'hidden',
                  background: '#252532',
                  border: '1px solid #2a2a3a'
                }}
                headStyle={{ borderBottom: '1px solid #2a2a3a' }}
                extra={
                  <Space size="small">
                    {video ? (
                      <>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleVideoDownload(video.video_path)}
                          style={{ background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
                        >
                          下载
                        </Button>
                        {!isOrderCompleted && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleVideoDelete(video.id)}
                          >
                            删除
                          </Button>
                        )}
                      </>
                    ) : (
                      !isOrderCompleted && (
                        <Upload
                          accept="video/*"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            handleVideoUpload(file, episode);
                            return false;
                          }}
                        >
                          <Button
                            size="small"
                            icon={<UploadOutlined />}
                            loading={uploading}
                            style={{ background: '#00d4aa', borderColor: '#00d4aa', color: '#0d0d12' }}
                          >
                            上传
                          </Button>
                        </Upload>
                      )
                    )}
                  </Space>
                }
              >
                {video ? (
                  <div style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}>
                    <video
                      src={buildVideoUrl(video.video_path)}
                      controls
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    background: '#1a1a24',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #3a3a4a'
                  }}>
                    <div style={{ color: '#6c6c7d', textAlign: 'center' }}>
                      <UploadOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <div>点击上传成片</div>
                    </div>
                  </div>
                )}
                
                <div style={{ marginTop: 12 }}>
                  {order?.review_comment && parseReviewComment(order.review_comment).episodes[String(episode)] && (
                    <div style={{ padding: 8, background: '#1a1a24', borderRadius: 4, border: '1px solid #2a2a3a', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <div style={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%',
                          background: '#ff9800',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <UserOutlined style={{ color: '#0d0d12', fontSize: 12 }} />
                        </div>
                        <div style={{ color: '#ff9800', fontSize: 12 }}>发布者审核意见</div>
                      </div>
                      <div style={{ color: '#e8e8ec', fontSize: 12 }}>
                        {parseReviewComment(order.review_comment).episodes[String(episode)]}
                      </div>
                    </div>
                  )}
                  
                  {(episodeComments[String(episode)] && episodeComments[String(episode)].length > 0) && (
                    <div style={{ padding: 8, background: '#1a1a24', borderRadius: 4, border: '1px solid #2a2a3a', marginBottom: 8 }}>
                      {episodeComments[String(episode)].map(comment => (
                        <div 
                          key={comment.id} 
                          style={{ 
                            marginBottom: 8,
                            display: 'flex',
                            gap: 8
                          }}
                        >
                          <div style={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%',
                            background: comment.user.role === 'publisher' ? '#ff9800' : '#00d4aa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <UserOutlined style={{ color: '#0d0d12', fontSize: 12 }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: 11, 
                              color: comment.user.role === 'publisher' ? '#ff9800' : '#00d4aa',
                              marginBottom: 2
                            }}>
                              {comment.user.nickname} ({comment.user.role === 'publisher' ? '发布者' : '创作者'})
                            </div>
                            <div style={{ 
                              fontSize: 12, 
                              color: '#e8e8ec',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {comment.content}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id={`comment_input_${episode}`}
                      type="text"
                      placeholder="回复发布者..."
                      style={{ 
                        flex: 1,
                        background: '#252532', 
                        border: '1px solid #2a2a3a', 
                        color: '#e8e8ec',
                        borderRadius: 4,
                        padding: '6px 12px',
                        fontSize: 12,
                        outline: 'none'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddComment(episode);
                        }
                      }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      size="small"
                      loading={commentLoading[episode]}
                      onClick={() => handleAddComment(episode)}
                      style={{ 
                        background: '#00d4aa', 
                        borderColor: '#00d4aa', 
                        color: '#0d0d12',
                        padding: '0 12px'
                      }}
                    >
                      发送
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {order?.review_comment && parseReviewComment(order.review_comment).overall && (
          <div style={{ marginTop: 16, padding: 12, background: '#1a1a24', borderRadius: 8, border: '1px solid #2a2a3a' }}>
            <div style={{ color: '#ff9800', fontSize: 14, marginBottom: 8 }}>📝 整体审核意见</div>
            <div style={{ color: '#e8e8ec', fontSize: 13, whiteSpace: 'pre-wrap' }}>
              {parseReviewComment(order.review_comment).overall}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FinalReviewPage;